import { type ApolloQueryResult, useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Accordion,
  AccordionItem,
  Details,
  Divider,
  type FormActionData,
  GenericField,
  IconName,
  InfoPanel,
  InlineMenu,
  Paragraph,
  ReadOnlyField,
  Section,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  RmqConnectionDocument,
  type RmqConnectionPayload,
  type RmqConnectionQuery,
  type RmqConnectionQueryVariables,
  SetupRmqUserDocument,
  type SetupRmqUserMutation,
  type SetupRmqUserMutationVariables,
} from '../../../../generated/hosting-service';
import {
  DevSetupServiceAccountWithPermissionsDocument,
  type DevSetupServiceAccountWithPermissionsMutation,
  type DevSetupServiceAccountWithPermissionsMutationVariables,
} from '../../../../generated/id-service-auth-endpoint';
import {
  EnvironmentManagedServicesDocument,
  type EnvironmentManagedServicesQuery,
  type EnvironmentManagedServicesQueryVariables,
  EnvironmentServiceState,
  useEnvironmentConnectionQuery,
  useEnvironmentQuery,
} from '../../../../generated/id-service-env-administration';
import { authStateStore } from '../../../../state-store';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './EnvironmentConnection.module.scss';

const devServiceAccountName = 'dev-service-account';
const defaultClientSecret = '********************************';
const defaultValue = 'Not Available';
let coreServicesInterval: NodeJS.Timeout;

type ServiceAccountData =
  | {
      id?: string;
      clientId?: string;
      clientSecret?: string;
    }
  | undefined;

type RMQConnectionData = Pick<
  RmqConnectionPayload,
  'rmqVHost' | 'rmqUser' | 'rmqPassword'
>;

type RegenerateTarget = 'ALL' | 'RMQ' | 'SERVICE_ACCOUNT';

interface FormData {
  serviceAccount: ServiceAccountData;
  rmqConfig: RMQConnectionData;
}

export const EnvironmentConnection: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const apolloClient = useApolloClient();

  // Query environment connection data
  const { loading, data, error } = useEnvironmentConnectionQuery({
    client: apolloClient,
    variables: {
      environmentId: envId,
      serviceAccountCondition: {
        name: devServiceAccountName,
        environmentId: envId,
      },
    },
    fetchPolicy: 'no-cache',
  });

  // Query Environment Name
  const environmentDetails = useEnvironmentQuery({
    client: apolloClient,
    variables: {
      id: envId,
    },
    fetchPolicy: 'no-cache',
  });
  const envName = environmentDetails.data?.environment?.name;

  // State of 'Regenerate Connection' action
  const [allowRegenerate, setAllowRegenerate] = useState<boolean>(false);

  // State of whether a new environment connection is generated
  const [isNewConnection, setIsNewConnection] = useState<boolean>(false);

  // State of the development service account for the environment
  const [serviceAccount, setServiceAccount] = useState<ServiceAccountData>({
    id: defaultValue,
    clientId: defaultValue,
    clientSecret: defaultValue,
  });

  // State of the RabbitMQ configuration for the environment
  const [rmqConfig, setRmqConfig] = useState<RMQConnectionData>({
    rmqVHost: defaultValue,
    rmqUser: defaultValue,
    rmqPassword: defaultValue,
  });

  /**
   * Check whether all core services of the environment are enabled
   */
  const isCoreServicesEnabled = useCallback(async () => {
    const res = await apolloClient.query<
      EnvironmentManagedServicesQuery,
      EnvironmentManagedServicesQueryVariables
    >({
      query: EnvironmentManagedServicesDocument,
      variables: {
        condition: {
          environmentId: envId,
        },
        filter: {
          isCoreService: {
            equalTo: true,
          },
        },
      },
      fetchPolicy: 'no-cache',
    });

    return (
      res.data.environmentManagedServices?.nodes
        .map((service) => service.state === EnvironmentServiceState.Enabled)
        .reduce((a, b) => a && b) ?? false
    );
  }, [apolloClient, envId]);

  /**
   * Wait for core services of the environment to enable
   * @param callback This will be called once all the core services of the environment are enabled
   */
  const waitForCoreServices = useCallback(
    async (callback: () => void) => {
      let coreServicesEnabled = await isCoreServicesEnabled();

      if (coreServicesEnabled === false) {
        setMessage({
          title:
            'Please wait while the core services are enabled to generate the environment connection.',
          type: 'info',
        });

        coreServicesInterval = setInterval(async () => {
          coreServicesEnabled = await isCoreServicesEnabled();

          if (coreServicesEnabled === true) {
            clearInterval(coreServicesInterval);
            setMessage(undefined);
            callback();
          }
        }, 1000);
      } else {
        callback();
      }
    },
    [isCoreServicesEnabled],
  );

  /**
   * Regenerate environment connection for the environment
   */
  const regenerateConnection = useCallback(
    (target: RegenerateTarget = 'ALL') => {
      waitForCoreServices(async () => {
        // Generate RabbitMQ configuration for the environment
        if (target === 'ALL' || target === 'RMQ') {
          const hostingApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );

          const rmqResponse = await hostingApolloClient.mutate<
            SetupRmqUserMutation,
            SetupRmqUserMutationVariables
          >({
            mutation: SetupRmqUserDocument,
            fetchPolicy: 'no-cache',
          });

          const rmqConfig = rmqResponse.data?.setupRMQUser;
          if (rmqConfig) {
            setRmqConfig(rmqConfig);
            setIsNewConnection(true);
          }
        }

        // Generate development service account for the environment
        if (target === 'ALL' || target === 'SERVICE_ACCOUNT') {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.authBaseUrl).toString(),
          );

          const serviceAccountResponse = await envApolloClient.mutate<
            DevSetupServiceAccountWithPermissionsMutation,
            DevSetupServiceAccountWithPermissionsMutationVariables
          >({
            mutation: DevSetupServiceAccountWithPermissionsDocument,
            variables: {
              input: {
                serviceAccountName: devServiceAccountName,
                permissionStructure: [
                  {
                    serviceId: ManagedServiceId.ID_SERVICE,
                    permissions: [
                      'DEV_SERVICE_ACCOUNTS_SETUP_WITH_PERMISSIONS',
                      'DEV_ACCESS_TOKENS_GENERATE_WITH_PERMISSIONS',
                    ],
                  },
                ],
                enforceValidPermissionStructure: false,
              },
            },
            fetchPolicy: 'no-cache',
          });

          const serviceAccount =
            serviceAccountResponse.data?.devSetupServiceAccountWithPermissions;
          if (serviceAccount) {
            setServiceAccount(serviceAccount);
            setIsNewConnection(true);
          }
        }
      });
    },
    [envId, waitForCoreServices],
  );

  // Display information message if the environment type is production
  useEffect(() => {
    if (data?.environment?.isDevelopment === false) {
      setMessage({
        title:
          'The environment connection details must be created manually via the corresponding workflows for a Production environment type.',
        type: 'info',
      });
    }
  }, [data?.environment?.isDevelopment]);

  // Get the RMQ Data for the environment
  useEffect(() => {
    (async () => {
      let rmqDetails: ApolloQueryResult<RmqConnectionQuery> | undefined;
      try {
        const hostingApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        rmqDetails = await hostingApolloClient.query<
          RmqConnectionQuery,
          RmqConnectionQueryVariables
        >({
          query: RmqConnectionDocument,
          fetchPolicy: 'no-cache',
        });
      } catch (_e) {
        // An error may occur if the Hosting Service is not yet started for the environment.
        // This can be handled silently, as through `regenerateConnection`, a new connection will
        // be made and the station will be updated accordingly through the state of `rmqConfig`.
      }

      if (rmqDetails?.data.rmqConnection) {
        setRmqConfig({
          ...rmqDetails.data.rmqConnection,
          rmqVHost: rmqDetails.data.rmqConnection.rmqVHost ?? defaultValue,
          rmqUser: rmqDetails.data.rmqConnection.rmqUser ?? defaultValue,
          rmqPassword:
            rmqDetails.data.rmqConnection.rmqPassword ?? defaultValue,
        });
      }

      if (data?.serviceAccounts?.nodes.length === 1) {
        setServiceAccount({
          ...data.serviceAccounts.nodes[0],
          clientSecret: defaultClientSecret,
        });
      }

      // Generate the development service account and RabbitMQ configuration if the environment does not have them
      if (data?.environment?.isDevelopment === true) {
        if (
          data.serviceAccounts?.nodes.length === 0 &&
          (rmqDetails?.data.rmqConnection?.rmqUser === null ||
            rmqDetails?.data.rmqConnection?.rmqUser === undefined)
        ) {
          regenerateConnection();
        } else {
          // Wait for core services to enable before enabling 'Regenerate Connection' action
          waitForCoreServices(async () => {
            setAllowRegenerate(true);
          });
        }
      }

      const vHost = rmqDetails?.data?.rmqConnection?.rmqVHost;

      if (rmqDetails?.data && (vHost === null || vHost === undefined)) {
        setMessage({
          title:
            'There was an error while generating the RabbitMQ connection. Please contact Axinom Support if the problem persists.',
          type: 'error',
        });
      }
    })();
  }, [
    data?.environment?.isDevelopment,
    data?.serviceAccounts?.nodes,
    envId,
    regenerateConnection,
    waitForCoreServices,
    isCoreServicesEnabled,
  ]);

  // Disable 'Generate Connection' action when a new connection is generated
  useEffect(() => {
    if (isNewConnection) {
      setAllowRegenerate(false);
    }
  }, [isNewConnection]);

  const { actions } = useGenerationActions(
    allowRegenerate,
    regenerateConnection,
  );

  // Stop polling for core services state when the component unmounts
  useEffect(() => {
    return () => {
      if (coreServicesInterval) {
        clearInterval(coreServicesInterval);
      }
    };
  }, []);

  const Panel: React.FC = () => {
    const tenantId = authStateStore.useState('tenantId');
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{tenantId}</Paragraph>
          <Paragraph title="Environment ID">{envId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    const tenantId = authStateStore.useState('tenantId');
    const { values } = useFormikContext<FormData>();

    const envSettings = `# Environment Settings (${envName})
TENANT_ID=${tenantId}
ENVIRONMENT_ID=${envId}
RABBITMQ_VHOST=${values.rmqConfig.rmqVHost}
RABBITMQ_USER=${values.rmqConfig.rmqUser}
RABBITMQ_PASSWORD=${values.rmqConfig.rmqPassword}
DEV_SERVICE_ACCOUNT_CLIENT_ID=${values.serviceAccount?.clientId}
DEV_SERVICE_ACCOUNT_CLIENT_SECRET=${values.serviceAccount?.clientSecret}`;

    const onCopyToClipboardClickedHandler = (): void => {
      try {
        // copy environment settings to clipboard
        navigator.clipboard.writeText(envSettings);
      } catch (_error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to copy environment settings to clipboard');
      }
    };

    const getConfigDownloadUrl = (): string => {
      const blobConfig = new Blob([envSettings], {
        type: 'text/plain;charset=utf-8',
      });

      return URL.createObjectURL(blobConfig);
    };

    return (
      <>
        {isNewConnection && (
          <>
            <GenericField
              name="environmentSettings"
              label="Environment Settings"
            >
              <div className={classes.secret}>
                <TextButton
                  className={classes.button}
                  text="Copy to Clipboard"
                  onButtonClicked={onCopyToClipboardClickedHandler}
                />
                <p>
                  <a
                    href={getConfigDownloadUrl()}
                    target="_blank"
                    rel="noreferrer"
                    download={`mosaic-environment-connection-${envName}.env`}
                  >
                    Download
                  </a>
                </p>
              </div>
            </GenericField>
            <Divider />
          </>
        )}

        <Accordion alignAccordionItem={false}>
          <AccordionItem
            header={
              <div className={classes.inline}>
                <span>RabbitMQ Properties</span>
                <InlineMenu
                  showArrow={false}
                  placement="bottom-end"
                  addBackgroundOpacity={true}
                  width="350px"
                  actions={[
                    {
                      label: 'Re-Generate RabbitMQ Connection',
                      onActionSelected: () => {
                        return regenerateConnection('RMQ');
                      },
                    },
                    {
                      label: 'View Details',
                      path: `/env/${envId}/services/ax-hosting-service/config/rabbitmq`,
                      icon: IconName.ChevronRight,
                    },
                  ]}
                />
              </div>
            }
          >
            <div className={clsx(classes.contentAccordion)}>
              <Field
                name="rmqConfig.rmqVHost"
                label="Vhost"
                as={ReadOnlyField}
              />
              <Field name="rmqConfig.rmqUser" label="User" as={ReadOnlyField} />
              <Field
                name="rmqConfig.rmqPassword"
                label="Password"
                as={ReadOnlyField}
              />
            </div>
          </AccordionItem>

          <AccordionItem
            header={
              <div className={classes.inline}>
                <span>Dev Service Account Properties</span>
                <InlineMenu
                  showArrow={false}
                  placement="bottom-end"
                  addBackgroundOpacity={true}
                  width="350px"
                  actions={[
                    {
                      label: 'Re-Generate Dev Service Connection',
                      onActionSelected: () => {
                        return regenerateConnection('SERVICE_ACCOUNT');
                      },
                    },
                    {
                      label: 'View Details',
                      path: `/env/${envId}/serviceAccounts/${serviceAccount?.id}`,
                      icon: IconName.ChevronRight,
                    },
                  ]}
                />
              </div>
            }
          >
            <div className={clsx(classes.contentAccordion)}>
              <Field
                name="serviceAccount.clientId"
                label="Client ID"
                as={ReadOnlyField}
              />
              <Field
                name="serviceAccount.clientSecret"
                label="Secret"
                as={ReadOnlyField}
              />
            </div>
          </AccordionItem>
        </Accordion>
      </>
    );
  };

  const [message, setMessage] = useState<StationMessage>();
  useEffect(() => {
    if (isNewConnection) {
      setMessage({
        title:
          'WARNING: Please copy the secrets displayed below and store them in a secure location or download the settings stored in a file and store it in a secure location. Once this station is closed, these values cannot be retrieved again and can only be replaced by generating a new environment connection.',
        type: 'warning',
      });
    } else {
      setMessage({
        title: (
          <span>
            Environment connection settings are needed for connecting this
            environment with your customized media-template solution. See more
            details at{' '}
            <a
              href="https://docs.axinom.com/platform/media-template/"
              target="_blank"
              rel="noreferrer"
            >
              docs.axinom
            </a>
            .
          </span>
        ),
        type: 'info',
      });
    }
  }, [isNewConnection]);

  return (
    <Details<FormData>
      defaultTitle="Environment Connection"
      subtitle="Page Info"
      actions={actions}
      initialData={{
        data: {
          serviceAccount,
          rmqConfig,
        },
        loading,
        error,
      }}
      saveData={() => Promise.resolve()}
      infoPanel={<Panel />}
      stationMessage={message}
    >
      <Form />
    </Details>
  );
};

const useGenerationActions = (
  allowRegenerate: boolean,
  regenerateConnection: (target?: RegenerateTarget) => void,
): {
  readonly actions: FormActionData<FormData>[];
} => {
  const actions: FormActionData<FormData>[] = [];
  if (allowRegenerate) {
    actions.push({
      label: 'Re-Generate Connection',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'This will regenerate a development service account and RabbitMQ resources for the environment.',
      },
      onActionSelected: () => {
        return regenerateConnection('ALL');
      },
    });
  }

  return {
    actions,
  } as const;
};

export const EnvironmentConnectionCrumb: BreadcrumbResolver = () =>
  'Environment Connection';
